/**
 * Admin — Enterprise & bulk inquiries.
 * Lists Waitlist rows (the upgrade-request form writes the full intake into
 * formData). This is the admin view that was missing, so inquiries are visible.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Trash2, RefreshCw, Mail, Building2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { ExpandableText } from '../../components/ui/expandable-text';
import api from '../../services/api';

export const Route = createFileRoute('/admin/upgrade-requests')({
  component: AdminUpgradeRequests,
});

interface FormData {
  name?: string;
  company?: string;
  role?: string;
  linkedinOrWebsite?: string;
  useCase?: string;
  videosPerMonth?: string;
  teamSize?: string;
  pricingPreferences?: string[];
  betaFitWhy?: string;
}

interface Waitlist {
  id: string;
  email: string;
  planInterest: string;
  source: string | null;
  userId: string | null;
  notes: string | null;
  formData: FormData | null;
  createdAt: string;
}

function AdminUpgradeRequests() {
  const [rows, setRows] = useState<Waitlist[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const r = await api.get('/admin/upgrade-requests');
      setRows(r.data);
    } catch (e) {
      console.error('Failed to load inquiries:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this inquiry?')) return;
    setDeleting(id);
    try {
      await api.delete(`/admin/upgrade-requests/${id}`);
      setRows((prev) => prev.filter((r) => r.id !== id));
    } catch (e) {
      console.error('Failed to delete:', e);
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  // Rows with a real intake form (enterprise/bulk) vs bare waitlist joins.
  const inquiries = rows.filter((r) => r.formData);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Enterprise &amp; bulk inquiries</h1>
        <p className="text-muted-foreground">
          {inquiries.length} inquir{inquiries.length === 1 ? 'y' : 'ies'} · {rows.length - inquiries.length} bare waitlist join{rows.length - inquiries.length === 1 ? '' : 's'}
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2"><Mail className="h-5 w-5" /> Inquiries</CardTitle>
            <Button variant="outline" size="sm" onClick={load}><RefreshCw className="h-4 w-4 mr-2" /> Refresh</Button>
          </div>
        </CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No inquiries yet</p>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name / Company</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Use case</TableHead>
                    <TableHead>Volume / Team</TableHead>
                    <TableHead>Needs</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => {
                    const f = r.formData || {};
                    return (
                      <TableRow key={r.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <div>{f.name || '—'}</div>
                              {f.company && <div className="text-xs text-muted-foreground">{f.company}{f.role ? ` · ${f.role}` : ''}</div>}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <a href={`mailto:${r.email}`} className="text-primary hover:underline">{r.email}</a>
                          {f.linkedinOrWebsite && (
                            <div className="text-xs text-muted-foreground truncate max-w-[160px]">{f.linkedinOrWebsite}</div>
                          )}
                        </TableCell>
                        <TableCell className="max-w-[240px] align-top">
                          <ExpandableText text={f.useCase || r.notes || '—'} clampLines={3} />
                        </TableCell>
                        <TableCell className="text-xs whitespace-nowrap">
                          {f.videosPerMonth ? `${f.videosPerMonth}/mo` : '—'}<br />
                          <span className="text-muted-foreground">{f.teamSize || ''}</span>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1 max-w-[180px]">
                            {(f.pricingPreferences || []).map((n) => (
                              <Badge key={n} variant="secondary" className="text-[10px]">{n.replace(/_/g, ' ')}</Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{r.source || '—'}</TableCell>
                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                          {new Date(r.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost" size="icon"
                            onClick={() => handleDelete(r.id)}
                            disabled={deleting === r.id}
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
